// helper/fuzzy.ts
// A small, dependency-free fuzzy-search helper tailored for German address/name use-cases.
//
// Key ideas:
// - Normalize both the query and candidates so that superficial differences (case, accents,
//   separators, German umlauts, Straße/Str. variants) don't break matches.
// - Prefer fast direct matches (exact, startsWith, includes) before invoking edit-distance.
// - Use a lightweight Damerau–Levenshtein similarity for resilient matches with early-typo handling.
//
// Public API:
// - buildFuzzyIndex(list, opts): Precompute normalized text for a list (for repeated queries).
// - suggestFuzzyFromIndex(term, index, opts): Rank items by similarity for a term.
// - suggestFuzzy(term, list, opts): One-off helper that builds the index on the fly.

/** Normalizer transforms an input string into a comparable representation. */
export type Normalizer = (s: string) => string;

// Built-in normalizer steps (compose as needed). Each step is small and easy to reason about.
/** Trims whitespace. */
export const trimNormalizer: Normalizer = (s) => (s ?? '').trim();

/** Removes diacritics by Unicode decomposition (e.g., "é" → "e"). */
export const stripDiacritics: Normalizer = (s) => (s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/** Lowercases input for case-insensitive comparisons. */
export const lowerCaseNormalizer: Normalizer = (s) => (s ?? '').toLowerCase();

/** Removes common separators so that "Muster-Str." ≈ "muster str" ≈ "musterstr". */
export const removeCommonSeparators: Normalizer = (s) => (s ?? '').replace(/[ \-._]/g, '');

/**
 * German umlauts & ß normalization:
 * - ä → ae, ö → oe, ü → ue
 * - Ä/Ö/Ü → ae/oe/ue (lowered to keep uniformity with lowerCase step)
 * - ß → ss
 * Apply this if you want to ignore umlaut spelling variants in matching.
 */
export const deUmlautNormalizer: Normalizer = (s) =>
  (s ?? '')
    .replace(/[ä]/g, 'ae')
    .replace(/[ö]/g, 'oe')
    .replace(/[ü]/g, 'ue')
    .replace(/[Ä]/g, 'ae')
    .replace(/[Ö]/g, 'oe')
    .replace(/[Ü]/g, 'ue')
    .replace(/[ß]/g, 'ss');

/**
 * German "Straße" normalization.
 * Unifies:
 * - "Straße", "Strasse", and abbreviations like "Str." or "Str" at word boundaries
 * into the canonical token "strasse" (lowercased later by the lowerCase step).
 */
export const deStreetSuffixNormalizer: Normalizer = (s) =>
  (s ?? '')
    // Vereinheitliche "Straße/Strasse/Str." zu "strasse"
    .replace(/straße/gi, 'strasse')
    .replace(/strasse/gi, 'strasse')
    // Abkürzungen ("Str.", "Str") nur bei Wort-/Trenn-Grenze
    .replace(/str\.(?=$|[\s,;:\/\-])/gi, 'strasse')
    .replace(/str(?=$|[\s,;:\/\-])/gi, 'strasse');

/**
 * Compose a pipeline of normalizers into a single function.
 * Example:
 *   const n = composeNormalizers([trimNormalizer, stripDiacritics, lowerCaseNormalizer])
 *   n('  Straße '); // => 'strasse'
 */
export function composeNormalizers(ns: Normalizer | Normalizer[]): Normalizer {
  const arr = Array.isArray(ns) ? ns : [ns];
  return (input: string) => arr.reduce((acc, fn) => fn(acc), input ?? '');
}

/**
 * Default normalizer balancing correctness and performance:
 * - trim → strip diacritics → lowercase → remove common separators.
 * This is suited for general autocomplete where spacing and accents shouldn't matter.
 */
export const defaultNormalizer: Normalizer = composeNormalizers([
  trimNormalizer,
  stripDiacritics,
  lowerCaseNormalizer,
  removeCommonSeparators,
]);

/**
 * Damerau–Levenshtein edit distance (optimal string alignment variant).
 * Counts minimal operations to transform a → b using insertion, deletion, substitution,
 * and transposition of adjacent characters. Lower is more similar.
 *
 * Complexity: O(|a| * |b|) time and space.
 *
 * Implementation notes:
 * - We use a classic dynamic programming matrix with an extra border.
 * - da records the last row index a character was seen at (needed for transpositions).
 * - db tracks the last column index where characters matched on the current row.
 */
function editDistance(a: string, b: string): number {
  const al = a.length,
    bl = b.length;
  const INF = al + bl; // A large sentinel value
  const da: Record<string, number> = {};
  // Matrix with two extra rows/cols for sentinel borders
  const d: number[][] = Array.from({ length: al + 2 }, () => Array(bl + 2).fill(0));

  // Initialize sentinel borders
  (d[0] as any)[0] = INF;
  for (let i = 0; i <= al; i++) {
    (d[i + 1] as any)[1] = i;
    (d[i + 1] as any)[0] = INF;
  }
  for (let j = 0; j <= bl; j++) {
    (d[1] as any)[j + 1] = j;
    (d[0] as any)[j + 1] = INF;
  }

  // Fill DP matrix
  for (let i = 1; i <= al; i++) {
    let db = 0; // last matching column index in this row
    const aiChar = a.charAt(i - 1);
    for (let j = 1; j <= bl; j++) {
      const bjChar = b.charAt(j - 1);
      const i1 = da[bjChar] ?? 0; // last row with this char in 'a'
      const j1 = db; // last column in this row with a match
      const same = aiChar === bjChar;
      const cost = same ? 0 : 1;
      if (same) db = j;
      (d[i + 1] as any)[j + 1] = Math.min(
        (d[i] as any)[j] + cost, // substitution
        (d[i + 1] as any)[j] + 1, // insertion
        (d[i] as any)[j + 1] + 1, // deletion
        // transposition cost if previous occurrences allow it
        (d[i1] as any)[j1] + (i - i1 - 1) + 1 + (j - j1 - 1)
      );
    }
    da[aiChar] = i;
  }
  return (d[al + 1] as any)[bl + 1] as number;
}

/**
 * Convert edit distance into a normalized similarity in [0, 1].
 * 1 means identical, 0 means completely different (given our normalization).
 */
export function stringSimilarity(a: string, b: string): number {
  const dist = editDistance(a, b);
  const maxLen = Math.max(a.length, b.length);
  return maxLen === 0 ? 1 : 1 - dist / maxLen;
}

/** Preprocessed candidate with raw and normalized text. */
export type FuzzyItem<T> = { item: T; text: string; norm: string };
/** An index is simply an array of preprocessed items for fast querying. */
export type FuzzyIndex<T> = FuzzyItem<T>[];

/**
 * Options for fuzzy suggestions.
 * - getText: how to extract text from an item (defaults to String(item)).
 * - limit: max results to return.
 * - threshold: minimum similarity score (0..1) to include fuzzy matches.
 * - normalizers / normalizer: normalization pipeline (pipeline preferred over single).
 * - returnOnEmpty: when the normalized query is empty, return the first items (score 0).
 */
export type FuzzyOptions<T> = {
  getText?: (item: T) => string;
  limit?: number;
  threshold?: number;
  // New: pipeline of normalizers
  normalizers?: Normalizer[];
  // Back-compat: single normalizer
  normalizer?: Normalizer;
  returnOnEmpty?: boolean;
};

// Reasonable defaults for autocomplete-style UIs
const DEFAULT_LIMIT = 8;
const DEFAULT_THRESHOLD = 0.72;

// Internal: choose effective normalizer based on provided options
function getEffectiveNormalizer(opts: Pick<FuzzyOptions<any>, 'normalizer' | 'normalizers'>): Normalizer {
  if (opts.normalizers && opts.normalizers.length > 0) {
    return composeNormalizers(opts.normalizers);
  }
  if (opts.normalizer) return opts.normalizer;
  return defaultNormalizer;
}

/**
 * Precompute an index from a list of items.
 * Useful when you need to call suggest multiple times with different query terms.
 */
export function buildFuzzyIndex<T>(
  list: T[],
  opts: Pick<FuzzyOptions<T>, 'getText' | 'normalizer' | 'normalizers'> = {}
): FuzzyIndex<T> {
  const getText = (opts.getText ?? ((i: unknown) => String(i))) as (item: T) => string;
  const normalizer = getEffectiveNormalizer(opts);
  return list.map((item) => {
    const text = getText(item);
    const norm = normalizer(text);
    return { item, text, norm };
  });
}

/** A single suggestion with an item and its similarity score. */
export type SuggestResult<T> = { item: T; score: number };

/**
 * Suggest items from a pre-built index for a given term.
 *
 * Strategy:
 * 1) Normalize the query. If empty, optionally return the first items (score 0).
 * 2) Try cheap direct matches (exact/startsWith/includes). If any, return those.
 * 3) Try a prefix-window fuzzy check (compare query vs candidate prefix qLen+2) to
 *    handle early typos efficiently.
 * 4) As a last step, do a full-length fuzzy match on length-pruned candidates.
 */
export function suggestFuzzyFromIndex<T>(
  term: string,
  index: FuzzyIndex<T>,
  opts: Pick<FuzzyOptions<T>, 'limit' | 'threshold' | 'normalizer' | 'normalizers' | 'returnOnEmpty'> = {}
): SuggestResult<T>[] {
  const limit = opts.limit ?? DEFAULT_LIMIT;
  const threshold = opts.threshold ?? DEFAULT_THRESHOLD;
  const normalizer = getEffectiveNormalizer(opts);
  const returnOnEmpty = opts.returnOnEmpty ?? true;

  // 1) Normalize query
  const qn = normalizer(term ?? '');
  if (!qn) {
    // Empty input: either return nothing or the first few items with score 0.
    if (!returnOnEmpty) return [];
    return index
      .slice(0, 200) // protect against extremely large indices
      .map((c) => ({ item: c.item, score: 0 }))
      .slice(0, limit);
  }

  // 2) Direct matches first: fast and usually good enough for short queries.
  const direct: SuggestResult<T>[] = [];
  for (const c of index) {
    let score = 0;
    if (c.norm === qn)
      score = 1; // exact match
    else if (c.norm.startsWith(qn))
      score = 0.95; // prefix match
    else if (c.norm.includes(qn)) score = 0.88; // substring match
    if (score > 0) direct.push({ item: c.item, score });
  }
  if (direct.length > 0) {
    return direct.sort((a, b) => b.score - a.score).slice(0, limit);
  }

  // 3) Fuzzy fallback with simple pruning focusing on early typos.
  const qLen = qn.length;
  // Make the threshold slightly more lenient for short queries.
  const prefixThreshold = threshold === DEFAULT_THRESHOLD ? (qLen <= 8 ? 0.7 : 0.72) : threshold;
  // Compare the query with the first qLen+2 chars of the candidate to allow small
  // early typos like missing 'h' in "…poehl…" vs "…poel…".
  const prefixWindowResults = index
    .map((c) => {
      const target = c.norm.slice(0, Math.min(c.norm.length, qLen + 2));
      return { item: c.item, score: stringSimilarity(qn, target) };
    })
    .filter((x) => x.score >= prefixThreshold);

  if (prefixWindowResults.length > 0) {
    return prefixWindowResults.sort((a, b) => b.score - a.score).slice(0, limit);
  }

  // 4) Full-length fuzzy match, but only for candidates with similar length (±3).
  return index
    .filter((c) => Math.abs(c.norm.length - qLen) <= 3)
    .map((c) => ({ item: c.item, score: stringSimilarity(qn, c.norm) }))
    .filter((x) => x.score >= threshold)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

/**
 * Convenience wrapper: build an index on the fly and return only the items (no scores).
 * If you query repeatedly on the same list, prefer buildFuzzyIndex + suggestFuzzyFromIndex.
 */
export function suggestFuzzy<T>(term: string, list: T[], opts: FuzzyOptions<T> = {}): T[] {
  const index = buildFuzzyIndex(list, {
    getText: opts.getText,
    normalizer: opts.normalizer,
    normalizers: opts.normalizers,
  });
  const results = suggestFuzzyFromIndex(term, index, {
    limit: opts.limit,
    threshold: opts.threshold,
    normalizer: opts.normalizer,
    normalizers: opts.normalizers,
    returnOnEmpty: opts.returnOnEmpty,
  });
  return results.map((r) => r.item);
}
